//src/pages/AdminCreatorInpit.jsx
import { useState, useEffect } from "react";
import API from "../services/api";
import "./AdminCreatorInput.css";

const initialForm = {
  creator_name: "",
  creator_url: "",
  platform: "TikTok",
  profile_image: "",
  followers: "",
  audience: "",
  avg_views: "",
  avg_likes: "",
  avg_comments: "",
  posting_frequency: "",
  best_posting_day: "",
  best_posting_time: "",
  niche: "",
  content_style: "",
  tone: "",
  top_hook: "",
  call_to_action: "",
  viral_topics: "",
  brand_fit: 0,
  score: 0,
  notes: ["", "", "", "", ""], // bullet points
  last_video_url: "",
  videos_analyzed: 0
};

export default function  AdminCreatorInput({ creator, onFinish }) {
  const [form, setForm] = useState(initialForm);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  // ✅ useEffect for editing existing creator
  useEffect(() => {
    if (creator) {
      setForm({
        ...initialForm,
        ...creator,
        notes: typeof creator.notes === "string"
          ? creator.notes.split("\n").map(n => n.replace(/^- /, ""))
          : creator.notes
      });
      setIsEdit(true);
    } else {
      setForm(initialForm);
      setIsEdit(false);
    }
  }, [creator]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleNoteChange = (i, value) => {
    const notes = [...form.notes];
    notes[i] = value;
    setForm({ ...form, notes });
  };

  // Auto-calculated Brand Fit
  const calculateBrandFit = () => {
    const scores = ["professionalism","consistency","audience_quality","content_quality","brand_safety"]
      .map(field => {
        const val = form[field];
        if (val === "Poor") return 20;
        if (val === "Average") return 60;
        if (val === "Excellent") return 100;
        return 0;
      });
    return Math.round(scores.reduce((a,b)=>a+b,0)/scores.length);
  };

  // Auto-calculated Overall Score
  const calculateOverallScore = () => {
    const fields = ["engagement","posting_consistency","production_quality","growth_potential","audience_relevance"];
    const scores = fields.map(field => {
      const val = form[field];
      if (val === "Poor") return 20;
      if (val === "Average") return 60;
      if (val === "Excellent") return 100;
      return 0;
    });
    return Math.round(scores.reduce((a,b)=>a+b,0)/scores.length);
  };
  const handleSubmit = async () => {
    setLoading(true);
    setSuccess(false);
    try {
      const payload = {
        ...form,
        followers: Number(form.followers || 0),
        avg_views: Number(form.avg_views || 0),
        avg_likes: Number(form.avg_likes || 0),
        avg_comments: Number(form.avg_comments || 0),
        posting_frequency: Number(form.posting_frequency || 0),
        brand_fit: calculateBrandFit(),
        score: calculateOverallScore(),
        notes: form.notes.filter(n => n.trim() !== "").map(n => `- ${n}`).join("\n"),
        source:"manual"
      };

      if (payload.avg_likes > payload.avg_views) {
        alert("Average Likes cannot exceed Average Views.");
        setLoading(false);
        return;
      }
      if (payload.avg_comments > payload.avg_views) {
        alert("Comments cannot exceed Views.");
        setLoading(false);
        return;
      }
      if (payload.top_hook.length < 15) {
        alert("Top Hook should be at least 15 characters.");
        setLoading(false);
        return;
      }
      if (!payload.last_video_url) {
        alert("Last Video URL is required.");
        setLoading(false);
        return;
      }
      if (payload.videos_analyzed < 10) {
        alert("At least 10 videos must be analyzed.");
        setLoading(false);
        return;
      }

      const res = isEdit
        ? await API.put(`/creators/${form.id}`, payload)
        : await API.post("/creators/", payload);

      if (res && res.data) {
        setSuccess(true);
        setForm(initialForm); // resets notes back to array
        setStep(1);
        onFinish && onFinish(res.data.creator);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to save creator.");
    }
    setLoading(false);
  };

  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  return (
    <div className="creator-form">
      <h1>🧠 Creator Intelligence Wizard</h1>
      <p>Step {step} of 5</p>

      {step === 1 && (
        <div>
          <h2>Basic Information</h2>
          <input name="creator_name" placeholder="Creator Name" value={form.creator_name} onChange={handleChange} />
          <input name="creator_url" placeholder="Creator URL" value={form.creator_url} onChange={handleChange} />
          <select name="platform" value={form.platform} onChange={handleChange}>
            <option>TikTok</option><option>Instagram</option><option>YouTube</option>
            <option>Twitter</option><option>LinkedIn</option><option>Facebook</option>
          </select>
          <input name="profile_image" placeholder="Profile Image URL" value={form.profile_image} onChange={handleChange} />
          <button onClick={nextStep}>Next ➡️</button>
        </div>
      )}

      {step === 2 && (
        <div>
          <h2>Audience & Metrics</h2>
          <input type="number" name="followers" placeholder="Followers" value={form.followers} onChange={handleChange} />
          <select name="audience" value={form.audience} onChange={handleChange}>
            <option value="">Select Audience</option>
            <option>Gen Z</option><option>Millennials</option><option>Parents</option>
            <option>Professionals</option><option>Students</option><option>Beauty Enthusiasts</option>
            <option>Fashion Enthusiasts</option><option>Fitness Enthusiasts</option><option>Food Lovers</option>
            <option>Gamers</option><option>Tech Enthusiasts</option><option>Small Business Owners</option>
            <option>General Entertainment</option>
          </select>
          <input type="number" name="avg_views" placeholder="Average Views" value={form.avg_views} onChange={handleChange} />
          <input type="number" name="avg_likes" placeholder="Average Likes" value={form.avg_likes} onChange={handleChange} />
          <input type="number" name="avg_comments" placeholder="Average Comments" value={form.avg_comments} onChange={handleChange} />
          <input type="number" name="videos_analyzed" placeholder="Videos Analyzed" value={form.videos_analyzed} onChange={handleChange} />
          <input name="last_video_url" placeholder="Last Analyzed Video URL" value={form.last_video_url} onChange={handleChange}/>
          <button onClick={prevStep}>⬅️ Back</button>
          <button onClick={nextStep}>Next ➡️</button>
        </div>
      )}

      {step === 3 && (
        <div>
          <h2>Content Analysis</h2>
          <select name="niche" value={form.niche} onChange={handleChange}>
            <option value="">Select Niche</option>
            <option>Beauty</option><option>Fashion</option><option>Finance</option>
            <option>Food</option><option>Travel</option><option>Gaming</option>
            <option>Education</option><option>Business</option><option>Fitness</option>
            <option>Parenting</option><option>Technology</option><option>Entertainment</option><option>Music</option>
          </select>
          <select name="content_style" value={form.content_style} onChange={handleChange}>
            <option value="">Select Style</option>
            <option>Storytelling</option><option>Educational</option><option>Comedy</option>
            <option>Product Reviews</option><option>Tutorials</option><option>Lifestyle Vlogs</option>
            <option>Interviews</option><option>Motivational</option><option>Talking Head</option><option>Day in the Life</option>
          </select>
          <select name="tone" value={form.tone} onChange={handleChange}>
            <option value="">Select Tone</option>
            <option>Funny</option><option>Professional</option><option>Friendly</option>
            <option>Inspirational</option><option>Luxury</option><option>Casual</option>
            <option>High Energy</option><option>Informative</option>
          </select>
          <textarea rows={3} name="top_hook" placeholder="Top Hook" value={form.top_hook} onChange={handleChange} />
          <select name="call_to_action" value={form.call_to_action} onChange={handleChange}>
            <option value="">Select CTA</option>
            <option>Follow for more</option><option>Comment below</option><option>Link in bio</option>
            <option>Shop now</option><option>Save this</option><option>Share with a friend</option><option>None</option>
            <option>Other</option>
          </select>
          {form.call_to_action === "Other" && (
            <input name="cta_other" placeholder="Custom CTA" value={form.cta_other || ""} onChange={handleChange} />
          )}
          <textarea rows={3} name="viral_topics" placeholder="Viral Topics" value={form.viral_topics} onChange={handleChange} />
          <button onClick={prevStep}>⬅️ Back</button>
          <button onClick={nextStep}>Next ➡️</button>
        </div>
      )}

      {step === 4 && (
        <div>
          <h2>Brand Evaluation</h2>
          <p>Rate each dimension:</p>
          {["professionalism","consistency","audience_quality","content_quality","brand_safety"].map(field=>(
            <div key={field}>
              <label>{field.replace("_"," ").toUpperCase()}</label>
              <select name={field} value={form[field]||""} onChange={handleChange}>
                <option value="">Select</option>
                <option>Poor</option><option>Average</option><option>Excellent</option>
              </select>
            </div>
          ))}
          <h3>Calculated Brand Fit: {calculateBrandFit()}</h3>

          <p>Overall Score Inputs (0–100):</p>
          {["engagement", "posting_consistency","production_quality","growth_potential","audience_relevance"].map(field=>(
            <div key={field}>
              <label>{field.replace("_"," ").toUpperCase()}</label>
              <select name={field} value={form[field]||""} onChange={handleChange}>
                <option value="">Select</option>
                <option value="Poor">Poor</option>
                <option value="Average">Average</option>
                <option value="Excellent">Excellent</option>
              </select>
            </div>
          ))}
          <h3>Calculated Overall Score: {calculateOverallScore()}</h3>

          <button onClick={prevStep}>⬅️ Back</button>
          <button onClick={nextStep}>Next ➡️</button>
        </div>
      )}

      {step === 5 && (
        <div>
          <h2>Review & Save</h2>
          <p>Complete bullet‑point notes:</p>
          {form.notes.map((note,i)=>(
            <input key={i} placeholder={`Observation ${i+1}`} value={note} onChange={e=>handleNoteChange(i,e.target.value)} />
          ))}

          <h3>Quality Checklist</h3>
          <ul>
            <li>☑ Name verified</li>
            <li>☑ Followers numeric</li>
            <li>☑ ≥10 videos reviewed</li>
            <li>☑ Hook copied</li>
            <li>☑ CTA verified</li>
            <li>☑ Brand Fit reviewed</li>
            <li>☑ Notes completed</li>
          </ul>

          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              padding: 15,
              background: "#f97316",
              color: "white",
              border: "none",
              borderRadius: 8,
              fontWeight: "bold",
              cursor: "pointer",
              fontSize: 16,
            }}
          >
            {loading ? "Saving..." : "Save Creator"}
          </button>

          {success && <div style={{ color: "green", fontWeight: "bold" }}>✅ Creator saved successfully.</div>}
          <button onClick={prevStep}>⬅️ Back</button>
        </div>
      )}
    </div>
  );
}